import { Group, noop } from "@mantine/core"
import { useDebouncedValue } from "@mantine/hooks"
import { Table } from "@tanstack/react-table"
import { ColumnManager, SearchInput } from "@tfg/ui"
import { Dispatch, SetStateAction, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { TABLE_CONTEXT_MAP } from "@tfg/types/schemas/modules/view/constants/table-context-map"

import { SortBuilder } from "~/components/SortBuilder"
import { FilterBuilderWrapper } from "~/components/table/FilterBuilderWrapper"
import { ViewHeader } from "~/components/table/views/ViewHeader"
import {
  FindContactRequestFiltersDto,
  FindContactRequestSortsDto,
  FindContactResponseDto,
} from "~/gen/api"
import { useGetColumnManagerTranslations } from "~/hooks/table"
import { useBuildBusinessPartnerFilters } from "~/pages/Brand/Suppliers/SuppliersRepository/hooks/useBuildBusinessPartnerFilters"

export const SupplierToolBar = ({
  table,
  isColumnManagerOpen,
  setIsColumnManagerOpen,
  searchSupplier,
  setSearchSupplier,
  sorts,
  setSorts,
  sortOptions,
}: {
  table: Table<FindContactResponseDto>
  isColumnManagerOpen: boolean
  setIsColumnManagerOpen: Dispatch<SetStateAction<boolean>>
  searchSupplier: string
  setSearchSupplier: Dispatch<SetStateAction<string>>
  sorts: FindContactRequestSortsDto
  setSorts: Dispatch<SetStateAction<FindContactRequestSortsDto>>
  sortOptions: { label: string; value: string }[]
}) => {
  const [localSearch, setLocalSearch] = useState<string>(searchSupplier)

  const columnManagerTranslations = useGetColumnManagerTranslations()

  const [debouncedSearch] = useDebouncedValue<string>(localSearch, 600)

  const { t } = useTranslation("organizationSettings")

  const { filters, setFilters, filterOptions } =
    useBuildBusinessPartnerFilters()

  useEffect(() => {
    setSearchSupplier(debouncedSearch)
  }, [debouncedSearch, setSearchSupplier])

  useEffect(() => {
    setLocalSearch(searchSupplier)
  }, [searchSupplier])

  if (!table) return null

  return (
    <Group w="100%" justify="left">
      <SearchInput
        inputValue={localSearch}
        onChange={(value) => setLocalSearch(value)}
        onOpen={noop}
        placeholder={t("supplierSettings.searchSuppliers")}
      />
      <SortBuilder sorts={sorts} setSorts={setSorts} options={sortOptions} />
      <FilterBuilderWrapper<FindContactRequestFiltersDto>
        options={filterOptions}
        filters={filters}
        setFilters={setFilters}
      />
      <ColumnManager
        table={table}
        translations={columnManagerTranslations}
        isOpen={isColumnManagerOpen}
        setIsOpen={setIsColumnManagerOpen}
      />
      <ViewHeader
        tableContext={TABLE_CONTEXT_MAP.BUSINESS_PARTNERS_REPOSITORY}
      />
    </Group>
  )
}
